import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { assignFindToBoard, deleteFind, getBoards, getFind, saveFindToBoard, updateFindImagePosition, updateFindImageUrl, updateFindTitle } from "../lib/api";
import { summarizeBoardPlacements } from "../lib/find-placements";
import { navigate } from "../lib/router";
import type { Board, ImagePosition, TriageAction, TriageProduct } from "../lib/types";
import { ImagePositionEditor } from "../components/ImagePositionEditor";
import { ProductImage, sourceLabelFor } from "../components/ProductImage";
import { ActionList, ActionListItem } from "../components/ActionList";
import { ExternalLink, LayoutGrid, Trash2, Plus, FolderPlus, BookmarkPlus } from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";

interface ProductTriageRouteProps {
  productId: number;
}

interface ProductCardPreviewProps {
  product: TriageProduct;
  onTitleChange?: (title: string) => void;
  onImagePositionChange?: (position: ImagePosition) => void;
  onImageUpload?: (imageUrl: string) => void;
}

function ProductCardPreview({ product, onTitleChange, onImagePositionChange, onImageUpload }: ProductCardPreviewProps) {
  const scraping = product.status === "scraping";
  const titleRef = useRef<HTMLSpanElement>(null);
  const [editingImage, setEditingImage] = useState(false);

  const handleTitleBlur = useCallback(() => {
    const el = titleRef.current;
    if (!el) return;
    const trimmed = (el.textContent ?? "").trim();
    if (!trimmed) {
      el.textContent = product.title;
      return;
    }
    if (trimmed !== product.title) {
      onTitleChange?.(trimmed);
    }
  }, [product.title, onTitleChange]);

  const handleTitleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLSpanElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        e.currentTarget.blur();
      } else if (e.key === "Escape") {
        e.currentTarget.textContent = product.title;
        e.currentTarget.blur();
      }
    },
    [product.title],
  );

  const handleImageSave = useCallback(
    (position: ImagePosition) => {
      setEditingImage(false);
      onImagePositionChange?.(position);
    },
    [onImagePositionChange],
  );

  const canEditImage = !scraping && !!product.imageUrl && !!onImagePositionChange;

  return (
    <article className="product-card product-card-bg panel-surface grid auto-rows-min overflow-hidden rounded-[14px]">
      {scraping ? (
        <div className="product-image-bg w-full aspect-[4/3] animate-pulse" />
      ) : editingImage && product.imageUrl ? (
        <ImagePositionEditor
          imageUrl={product.imageUrl}
          title={product.title}
          initial={{
            imageScale: product.imageScale,
            imageOffsetX: product.imageOffsetX,
            imageOffsetY: product.imageOffsetY,
          }}
          onSave={handleImageSave}
          onCancel={() => setEditingImage(false)}
          onImageUpload={onImageUpload}
        />
      ) : (
        <div
          className={`relative group${canEditImage ? " cursor-pointer" : ""}`}
          onClick={canEditImage ? () => setEditingImage(true) : undefined}
          data-testid={canEditImage ? "image-edit-trigger" : undefined}
        >
          <ProductImage
            imageUrl={product.imageUrl}
            title={product.title}
            imageScale={product.imageScale}
            imageOffsetX={product.imageOffsetX}
            imageOffsetY={product.imageOffsetY}
          />
          {canEditImage && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/30 transition-colors">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-8 h-8 text-white opacity-0 group-hover:opacity-90 transition-opacity drop-shadow-lg"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="15 3 21 3 21 9" />
                <path d="m21 3-7 7" />
                <polyline points="9 21 3 21 3 15" />
                <path d="m3 21 7-7" />
              </svg>
            </div>
          )}
        </div>
      )}
      <div className="grid gap-[0.35rem] px-4 pt-[0.95rem] pb-4">
        {scraping ? (
          <>
            <div className="h-[1.2em] w-3/4 rounded bg-white/10 animate-pulse" />
            <div className="grid gap-[0.22rem] text-[0.93rem]">
              <div className="h-[1em] w-1/3 rounded bg-white/10 animate-pulse" />
              <span className="text-muted">{sourceLabelFor(product.url)}</span>
            </div>
          </>
        ) : (
          <>
            <span
              id="find-title"
              ref={titleRef}
              className={`font-semibold${onTitleChange ? " cursor-text" : ""}`}
              contentEditable={!!onTitleChange}
              suppressContentEditableWarning
              onBlur={handleTitleBlur}
              onKeyDown={handleTitleKeyDown}
            >
              {product.title}
            </span>
            <span className="text-[0.93rem] text-muted">{sourceLabelFor(product.url)}</span>
          </>
        )}
      </div>
    </article>
  );
}

function ProductDetailView({ product, isOwner, boards: userBoards, saving, onTitleChange, onImagePositionChange, onImageUpload, onDelete, onSaveToBoard, onSaveToNewBoard }: { product: TriageProduct; isOwner: boolean; boards: Board[]; saving: boolean; onTitleChange: (title: string) => void; onImagePositionChange: (position: ImagePosition) => void; onImageUpload: (imageUrl: string) => void; onDelete: () => void; onSaveToBoard: (boardId: number) => void; onSaveToNewBoard: () => void }) {
  const domain = sourceLabelFor(product.url);
  const placementSummary = summarizeBoardPlacements(product.boardPlacements);

  return (
    <section
      className="w-full max-w-[960px] mx-auto px-4 pb-8 pt-[2.4rem] flex-1 min-h-0 overflow-auto grid grid-cols-[minmax(200px,320px)_1fr] gap-8 items-start max-[640px]:grid-cols-1"
    >
      <ProductCardPreview
        product={product}
        onTitleChange={isOwner ? onTitleChange : undefined}
        onImagePositionChange={isOwner ? onImagePositionChange : undefined}
        onImageUpload={isOwner ? onImageUpload : undefined}
      />

      <div className="grid gap-6 content-start">
        <ActionList>
          <ActionListItem icon={ExternalLink} href={product.url} external>
            Visit this on {domain}
          </ActionListItem>
          {product.boardPlacements.map((placement) => (
            <ActionListItem
              key={placement.boardId}
              icon={LayoutGrid}
              href={`/boards/${placement.boardId}`}
              onClick={(e) => {
                e.preventDefault();
                navigate(`/boards/${placement.boardId}`);
              }}
            >
              {isOwner ? `Open ${placement.boardLabel}` : `View ${placement.boardLabel}`}
            </ActionListItem>
          ))}
          {isOwner && (
            <ActionListItem icon={Trash2} onClick={onDelete}>
              Delete this
            </ActionListItem>
          )}
        </ActionList>

        {!isOwner && (
          <ActionList>
            {userBoards.map((board) => (
              <ActionListItem
                key={board.id}
                icon={Plus}
                disabled={saving}
                onClick={() => onSaveToBoard(board.id)}
              >
                Save to &lsquo;{board.label}&rsquo;
              </ActionListItem>
            ))}
            <ActionListItem
              icon={BookmarkPlus}
              disabled={saving}
              onClick={onSaveToNewBoard}
            >
              Save to a new board
            </ActionListItem>
          </ActionList>
        )}

        {placementSummary && (
          <p className="text-sm text-muted">
            Saved {placementSummary}
          </p>
        )}
      </div>
    </section>
  );
}

export function ProductTriageRoute({ productId }: ProductTriageRouteProps) {
  const [product, setProduct] = useState<TriageProduct | null>(null);
  const [isOwner, setIsOwner] = useState(true);
  const [userBoards, setUserBoards] = useState<Board[]>([]);
  const [assigning, setAssigning] = useState(false);
  const [newBoardDialogOpen, setNewBoardDialogOpen] = useState(false);
  const [newBoardLabel, setNewBoardLabel] = useState("");
  const [notFound, setNotFound] = useState(false);
  const pollRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const load = useCallback(async () => {
    const findData = await getFind(productId);
    if (!findData) {
      setNotFound(true);
      return;
    }

    const p = findData.find;
    setProduct(p);
    setIsOwner(findData.isOwner);

    if (!findData.isOwner) {
      const boardsData = await getBoards();
      if (boardsData) {
        setUserBoards(boardsData.boards);
      }
      return;
    }

    if (p.boardPlacements.length > 0) {
      return;
    }

    if (p.status === "scraping") {
      pollRef.current = setTimeout(load, 1500);
    }
  }, [productId]);

  useEffect(() => {
    load();
    return () => clearTimeout(pollRef.current);
  }, [load]);

  useEffect(() => {
    if (notFound) {
      navigate("/", true);
    }
  }, [notFound]);

  const handleTitleChange = useCallback(
    async (title: string) => {
      const result = await updateFindTitle(productId, title);
      if (result.ok) {
        setProduct((prev) => prev ? { ...prev, title } : prev);
      }
    },
    [productId],
  );

  const handleImagePositionChange = useCallback(
    async (position: ImagePosition) => {
      setProduct((prev) => prev ? { ...prev, ...position } : prev);
      await updateFindImagePosition(productId, position);
    },
    [productId],
  );

  const handleImageUpload = useCallback(
    async (imageUrl: string) => {
      setProduct((prev) => prev ? { ...prev, imageUrl, imageScale: 1, imageOffsetX: 0, imageOffsetY: 0 } : prev);
      await updateFindImageUrl(productId, imageUrl);
    },
    [productId],
  );

  const handleDelete = useCallback(async () => {
    const ok = await deleteFind(productId);
    if (ok) {
      navigate("/", true);
    }
  }, [productId]);

  const handleAssignToExisting = async (boardId: number) => {
    if (assigning) return;
    setAssigning(true);
    const result = await assignFindToBoard({ findId: productId, boardId });
    setAssigning(false);
    if (result.ok) {
      navigate(`/boards/${result.data.board.id}`);
    }
  };

  const handleCreateNewBoard = async (label: string) => {
    if (assigning) return;
    const trimmed = label.trim();
    if (!trimmed) return;
    setAssigning(true);
    const result = await assignFindToBoard({
      findId: productId,
      newBoard: { label: trimmed },
    });
    setAssigning(false);
    if (result.ok) {
      navigate(`/boards/${result.data.board.id}`);
    }
  };

  const handleManualNewBoardSubmit = async (e: FormEvent) => {
    e.preventDefault();
    await handleCreateNewBoard(newBoardLabel);
  };

  const handleTriageAction = (action: TriageAction) => {
    if (action.type === "existing_board") {
      void handleAssignToExisting(action.boardId);
      return;
    }
    void handleCreateNewBoard(action.label);
  };

  const handleSaveToBoard = async (boardId: number) => {
    if (assigning) return;
    setAssigning(true);
    const result = await saveFindToBoard({ sourceFindId: productId, boardId });
    setAssigning(false);
    if (result.ok) {
      navigate(`/boards/${result.data.board.id}`);
    }
  };

  const handleSaveToNewBoard = async () => {
    if (assigning || !product) return;
    setAssigning(true);
    const result = await saveFindToBoard({
      sourceFindId: productId,
      newBoard: { label: product.title },
    });
    setAssigning(false);
    if (result.ok) {
      navigate(`/boards/${result.data.board.id}`);
    }
  };

  if (!product) {
    return null;
  }

  if (product.boardPlacements.length > 0 || !isOwner) {
    return (
      <ProductDetailView
        product={product}
        isOwner={isOwner}
        boards={userBoards}
        saving={assigning}
        onTitleChange={handleTitleChange}
        onImagePositionChange={handleImagePositionChange}
        onImageUpload={handleImageUpload}
        onDelete={handleDelete}
        onSaveToBoard={handleSaveToBoard}
        onSaveToNewBoard={handleSaveToNewBoard}
      />
    );
  }

  const failed = product.status === "failed";
  const triageActions = product.triageActions.slice(0, 3);
  const triageActionSlots = Array.from({ length: 3 }, (_, index) => triageActions[index] ?? null);

  return (
    <section
      id="product-triage-view"
      className="w-full max-w-[960px] mx-auto px-4 pb-8 pt-[2.4rem] flex-1 min-h-0 overflow-auto grid grid-cols-[minmax(200px,320px)_1fr] gap-8 items-start max-[640px]:grid-cols-1"
    >
      <ProductCardPreview product={product} onTitleChange={handleTitleChange} onImagePositionChange={handleImagePositionChange} onImageUpload={handleImageUpload} />

      <div className="grid gap-6 content-start">
        {failed && (
          <p className="text-red-400 text-[0.93rem] m-0">
            We couldn't fetch details for this. You can still add it to a board.
          </p>
        )}

        <ActionList>
          {triageActionSlots.map((action, index) => {
            if (!action) {
              return (
                <ActionListItem
                  key={`reserved-${index}`}
                  icon={BookmarkPlus}
                  data-testid="triage-action-reserved"
                  disabled
                >
                  Add to board
                </ActionListItem>
              );
            }

            return (
              <ActionListItem
                key={action.type === "existing_board" ? `existing-${action.boardId}` : `new-${action.label}`}
                icon={action.type === "existing_board" ? Plus : FolderPlus}
                data-testid="triage-action"
                disabled={assigning}
                onClick={() => handleTriageAction(action)}
              >
                Add to &lsquo;{action.label}&rsquo; board
              </ActionListItem>
            );
          })}
          <ActionListItem
            icon={BookmarkPlus}
            data-testid="manual-new-board"
            disabled={assigning}
            onClick={() => {
              setNewBoardLabel(product.title);
              setNewBoardDialogOpen(true);
            }}
          >
            Add to new board
          </ActionListItem>
          <ActionListItem icon={Trash2} onClick={handleDelete}>
            Delete this
          </ActionListItem>
        </ActionList>
      </div>

      <Dialog open={newBoardDialogOpen} onOpenChange={setNewBoardDialogOpen}>
        <DialogContent>
          <form onSubmit={handleManualNewBoardSubmit}>
            <DialogHeader>
              <DialogTitle>Add to new board</DialogTitle>
            </DialogHeader>
            <Input
              autoFocus
              data-testid="new-board-name-input"
              value={newBoardLabel}
              onChange={(e) => setNewBoardLabel(e.currentTarget.value)}
            />
            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setNewBoardDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={assigning || !newBoardLabel.trim()}
                data-testid="new-board-submit"
              >
                Add
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  );
}
